// ============================================================================
// THE CYCLE — AI Search  (StatMuse-style NLQ search hero)
// Ask any baseball question → AI answers with your live Redis data
// ============================================================================

import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import {
  Box, Card, Typography, TextField, IconButton,
  CircularProgress, Fade, Chip, Stack, Alert, useTheme, alpha, Tooltip,
} from '@mui/material';
import {
  Search, Send, AutoAwesome, Refresh, SportsBaseball,
  ContentCopy, CheckCircle,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import {
  ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
  LineChart, Line, AreaChart, Area,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell,
} from 'recharts';
import { nlqApi } from '../services/apiService';
import { useYear } from '../context/YearContext';
import { useAuth } from '../context/AuthContext';
import { AuthModal, PaywallBanner } from '../components/AuthModal';

// ── Persist history in localStorage ─────────────────────────────────────────
const HISTORY_KEY = 'theCycle-aiSearch-history';
const MAX_HISTORY = 20;
function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; }
}
function saveHistory(answers) {
  try {
    const toSave = answers.filter(a => !a.loading && !a.error && a.answer).slice(0, MAX_HISTORY);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(toSave));
  } catch {}
}

// ── Follow-up suggestions by intent ─────────────────────────────────────────
const FOLLOW_UP_MAP = {
  player_stats:  ['How does that compare to league average?', 'Stats over the last 30 days?', 'Career stats?'],
  leaders:       ['Who are the bottom 5?', 'Same stat for the other position type?', 'Top 10 instead of 5?'],
  team_stats:    ['How does that compare to last season?', 'Best player on that team?', 'Pitching breakdown?'],
  standings:     ['Wild card picture?', 'Best run differential?', 'Last 10 games record?'],
  salary:        ['Who is overpaid relative to performance?', 'Best value contracts this year?', 'Biggest contracts expiring?'],
  matchup:       ['Historical head-to-head?', 'Home vs away splits?', 'Recent form for both?'],
  schedule:      ['Who has the easiest remaining schedule?', 'Back-to-back games this week?', 'Home vs away this week?'],
  default:       ['Tell me more about that.', 'Show me the top 5?', 'Compare to last season?'],
};
function getFollowUps(intent) {
  return (FOLLOW_UP_MAP[intent] || FOLLOW_UP_MAP.default).slice(0, 3);
}

// ── Chart palette (dynamic per series) ──────────────────────────────────────
const CHART_COLORS = ['#1976d2', '#d32f2f', '#2e7d32', '#ed6c02', '#9c27b0', '#0288d1', '#7b1fa2'];

// ── Chart renderer ──────────────────────────────────────────────────────────
const ChartRenderer = ({ chart }) => {
  const theme = useTheme();
  if (!chart) return null;
  // Heatmap uses chart.grid, gauge uses chart.percentile — others need chart.data
  const needsData = !['heatmap', 'gauge'].includes(chart.type);
  if (needsData && (!Array.isArray(chart.data) || chart.data.length === 0)) return null;

  const height = 240;
  const tickStyle = { fontSize: 11, fill: theme.palette.text.secondary };
  const gridStroke = alpha(theme.palette.text.primary, 0.08);

  const wrap = (inner) => (
    <Box sx={{ mt: 1.5, mb: 0.5 }}>
      {chart.title && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5, fontSize: '0.72rem' }}>
          {chart.title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>{inner}</ResponsiveContainer>
    </Box>
  );

  if (chart.type === 'bar') {
    const yKeys = chart.yKeys || [{ key: 'value', label: 'Value' }];
    return wrap(
      <BarChart data={chart.data} margin={{ top: 8, right: 8, left: -12, bottom: 24 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
        <XAxis dataKey={chart.xKey || 'name'} tick={tickStyle} interval={0} angle={-25} textAnchor="end" height={50} />
        <YAxis tick={tickStyle} />
        <RTooltip contentStyle={{ fontSize: 12 }} />
        {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {yKeys.map((y, i) => (
          <Bar key={y.key} dataKey={y.key} name={y.label || y.key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
        ))}
      </BarChart>
    );
  }

  if (chart.type === 'line') {
    const yKeys = chart.yKeys || [{ key: 'value', label: 'Value' }];
    return wrap(
      <LineChart data={chart.data} margin={{ top: 8, right: 8, left: -12, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
        <XAxis dataKey={chart.xKey || 'year'} tick={tickStyle} />
        <YAxis tick={tickStyle} />
        <RTooltip contentStyle={{ fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {yKeys.map((y, i) => (
          <Line key={y.key} type="monotone" dataKey={y.key} name={y.label || y.key}
            stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
        ))}
      </LineChart>
    );
  }

  if (chart.type === 'radar') {
    const keys = chart.keys || [];
    return wrap(
      <RadarChart data={chart.data} outerRadius="75%">
        <PolarGrid stroke={gridStroke} />
        <PolarAngleAxis dataKey="axis" tick={tickStyle} />
        <PolarRadiusAxis tick={tickStyle} angle={30} domain={[0, 100]} />
        <RTooltip contentStyle={{ fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {keys.map((k, i) => (
          <Radar key={k} name={k} dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]}
            fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.25} />
        ))}
      </RadarChart>
    );
  }

  if (chart.type === 'heatmap' && Array.isArray(chart.grid)) {
    const metric = chart.metric || 'ba';
    // Color scaling: red = hot, blue = cold. BA scale ~0..0.500. Whiff scale ~0..50%.
    const max = metric === 'whiffRate' ? 50 : 0.5;
    const min = 0;
    const cellColor = (val) => {
      if (!val || val <= min) return alpha(theme.palette.info.main, 0.18);
      const t = Math.min(1, Math.max(0, (val - min) / (max - min)));
      // gradient from cool (blue/grey) to hot (red)
      const hot = theme.palette.error.main;
      const cool = theme.palette.info.main;
      return t > 0.5 ? alpha(hot, 0.25 + 0.55 * (t - 0.5) * 2) : alpha(cool, 0.18 + 0.25 * (1 - t * 2));
    };
    const fmt = (val) => metric === 'whiffRate' ? `${(val || 0).toFixed(0)}%` : (val || 0).toFixed(3).replace(/^0/, '');
    const metricLabel = metric === 'whiffRate' ? 'Whiff%' : 'AVG';

    return (
      <Box sx={{ mt: 1.5, mb: 0.5 }}>
        {chart.title && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.25, fontSize: '0.72rem' }}>
            {chart.title}
          </Typography>
        )}
        {chart.subtitle && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.75, fontSize: '0.65rem' }}>
            {chart.subtitle}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 64px)',
            gridTemplateRows: 'repeat(3, 56px)',
            gap: '4px',
            border: `2px solid ${alpha(theme.palette.text.primary, 0.6)}`,
            borderRadius: 1,
            p: '4px',
            background: alpha(theme.palette.background.default, 0.5),
          }}>
            {chart.grid.flat().map((cell, idx) => {
              const v = cell?.[metric] || 0;
              return (
                <Box key={idx} sx={{
                  background: cellColor(v),
                  borderRadius: 0.5,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.78rem', fontWeight: 700,
                  color: theme.palette.text.primary,
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                }} title={`Zone ${cell?.id} · ${cell?.count || 0} pitches · ${cell?.hits || 0} hits`}>
                  <span>{fmt(v)}</span>
                  <span style={{ fontSize: '0.6rem', fontWeight: 400, opacity: 0.7 }}>n={cell?.count || 0}</span>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Cold</Typography>
            <Box sx={{
              width: 80, height: 8, borderRadius: 1,
              background: `linear-gradient(to right, ${alpha(theme.palette.info.main, 0.4)}, ${alpha(theme.palette.error.main, 0.8)})`,
            }} />
            <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Hot ({metricLabel})</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (chart.type === 'sparkline' && Array.isArray(chart.data) && chart.data.length >= 2) {
    const yKey = chart.yKey || 'value';
    const trendColor =
      chart.trend === 'heating up' || chart.trend === 'pitching better' ? theme.palette.success.main :
      chart.trend === 'cooling down' || chart.trend === 'struggling'    ? theme.palette.error.main :
                                                                          theme.palette.info.main;
    return wrap(
      <AreaChart data={chart.data} margin={{ top: 8, right: 8, left: -12, bottom: 8 }}>
        <defs>
          <linearGradient id="sparklineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={trendColor} stopOpacity={0.45} />
            <stop offset="100%" stopColor={trendColor} stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
        <XAxis dataKey={chart.xKey || 'idx'} tick={tickStyle} />
        <YAxis tick={tickStyle} />
        <RTooltip contentStyle={{ fontSize: 12 }} labelFormatter={(v, p) => p?.[0]?.payload?.date || `Game ${v}`} />
        <Area type="monotone" dataKey={yKey} stroke={trendColor} strokeWidth={2.2} fill="url(#sparklineGrad)" dot={{ r: 2 }} activeDot={{ r: 5 }} name={chart.yLabel || yKey} />
      </AreaChart>
    );
  }

  if (chart.type === 'donut' && Array.isArray(chart.data) && chart.data.length >= 2) {
    const palette = ['#1976d2', '#d32f2f', '#2e7d32', '#ed6c02', '#9c27b0', '#0288d1'];
    return (
      <Box sx={{ mt: 1.5, mb: 0.5 }}>
        {chart.title && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5, fontSize: '0.72rem' }}>
            {chart.title}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={chart.data}
              dataKey="value"
              nameKey="name"
              cx="50%" cy="50%"
              innerRadius={60} outerRadius={92}
              paddingAngle={2}
              label={(e) => `${e.name} ${(e.value || 0).toFixed(0)}%`}
              labelLine={false}
            >
              {chart.data.map((_, i) => (<Cell key={i} fill={palette[i % palette.length]} />))}
            </Pie>
            <RTooltip
              contentStyle={{ fontSize: 12 }}
              formatter={(v, n, ent) => {
                const p = ent?.payload || {};
                const lines = [`${(v || 0).toFixed(1)}%`];
                if (p.velo)  lines.push(`${p.velo} mph`);
                if (p.whiff) lines.push(`${p.whiff}% whiff`);
                if (p.count) lines.push(`${p.count} pitches`);
                return [lines.join(' · '), p.name];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    );
  }

  if (chart.type === 'gauge' && typeof chart.percentile === 'number') {
    const pct = Math.min(100, Math.max(0, chart.percentile));
    // Color gradient: red <30, amber 30-60, green 60-85, gold 85+
    const markerColor =
      pct >= 85 ? theme.palette.warning.main :
      pct >= 60 ? theme.palette.success.main :
      pct >= 30 ? theme.palette.info.main :
                  theme.palette.error.main;
    const mlbRank = chart.rank?.mlb?.rank;
    const mlbOf = chart.rank?.mlb?.of;
    return (
      <Box sx={{ mt: 1.5, mb: 0.5 }}>
        {chart.title && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5, fontSize: '0.72rem' }}>
            {chart.title}
          </Typography>
        )}
        <Box sx={{ position: 'relative', height: 38, mx: 1 }}>
          {/* Track */}
          <Box sx={{
            position: 'absolute', top: 14, left: 0, right: 0, height: 10, borderRadius: 5,
            background: `linear-gradient(to right,
              ${alpha(theme.palette.error.main, 0.6)} 0%,
              ${alpha(theme.palette.warning.main, 0.6)} 30%,
              ${alpha(theme.palette.info.main, 0.6)} 60%,
              ${alpha(theme.palette.success.main, 0.7)} 85%,
              ${alpha(theme.palette.warning.main, 0.85)} 100%)`,
          }} />
          {/* Marker */}
          <Box sx={{
            position: 'absolute', top: 8, left: `${pct}%`, transform: 'translateX(-50%)',
            width: 22, height: 22, borderRadius: '50%',
            background: markerColor,
            border: `3px solid ${theme.palette.background.paper}`,
            boxShadow: `0 0 0 1.5px ${markerColor}, 0 2px 6px ${alpha(theme.palette.common.black, 0.4)}`,
          }} />
          {/* Tick marks */}
          {[0, 25, 50, 75, 100].map(v => (
            <Box key={v} sx={{
              position: 'absolute', top: 26, left: `${v}%`, transform: 'translateX(-50%)',
              fontSize: '0.6rem', color: theme.palette.text.disabled,
            }}>{v}</Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 0.5, justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 700, color: markerColor }}>
            {pct.toFixed(0)}<sup style={{ fontSize: '0.55rem' }}>th</sup> percentile
          </Typography>
          {mlbRank && (
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              #{mlbRank} of {mlbOf} · {chart.stat}: <b>{chart.value}</b>
            </Typography>
          )}
        </Box>
        {chart.leader && chart.leader.name && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.25, fontSize: '0.65rem', color: 'text.disabled' }}>
            Leader: {chart.leader.name} ({chart.leader.value})
          </Typography>
        )}
      </Box>
    );
  }

  return null;
};

// ── Example prompts ─────────────────────────────────────────────────────────
const EXAMPLE_QUESTIONS = [
  'Who leads the AL in home runs?',
  'How is Shohei Ohtani doing this season?',
  'Best ERA in baseball right now?',
  'Who has the highest CVR?',
  'Top 5 stolen base leaders',
  'Which Dodgers hitter has the best OPS?',
  'Breakout players under $5M salary?',
  'Who are the hottest pitchers this week?',
];

// ── Answer bubble ────────────────────────────────────────────────────────────
const AnswerBubble = ({ question, answer, intent, charts, followUps: dynFollowUps, sampleSize, loading, error, onFollowUp }) => {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(answer).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const followUps = !loading && !error && answer
    ? ((dynFollowUps && dynFollowUps.length) ? dynFollowUps.slice(0, 4) : getFollowUps(intent))
    : [];

  return (
    <Fade in timeout={350}>
      <Card variant="outlined" sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha(accent, 0.25)}`,
        background: alpha(accent, 0.03),
        maxWidth: 680,
        mx: 'auto',
        mt: 2,
      }}>
        {/* Question header */}
        <Box sx={{
          px: 2.5, py: 1.25,
          borderBottom: `1px solid ${alpha(accent, 0.12)}`,
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <Search sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.82rem', flex: 1 }}>
            {question}
          </Typography>
          {!loading && !error && answer && (
            <Tooltip title={copied ? 'Copied!' : 'Copy answer'} arrow>
              <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? 'success.main' : 'text.disabled', p: 0.5 }}>
                {copied ? <CheckCircle sx={{ fontSize: 15 }} /> : <ContentCopy sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Answer body */}
        <Box sx={{ px: 2.5, py: 2, minHeight: 56 }}>
          {loading && !answer ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={16} thickness={4} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>Thinking…</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ py: 0.5, fontSize: '0.82rem' }}>{error}</Alert>
          ) : (
            <Box sx={{
              lineHeight: 1.7, fontSize: '0.97rem', color: 'text.primary',
              '& p': { m: 0, mb: 0.75 },
              '& ol, & ul': { pl: 2.5, my: 0.5 },
              '& li': { mb: 0.4 },
              '& strong': { color: 'text.primary', fontWeight: 700 },
              '& table': { borderCollapse: 'collapse', width: '100%', my: 1 },
              '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1.5, py: 0.5, fontSize: '0.88rem' },
              '& th': { bgcolor: 'action.hover', fontWeight: 700 },
            }}>
              <ReactMarkdown>{answer}</ReactMarkdown>
              {loading && (
                <Box component="span" sx={{
                  display: 'inline-block', width: '2px', height: '1em',
                  bgcolor: 'text.secondary', verticalAlign: 'text-bottom', ml: '2px',
                  animation: 'nlq-blink 0.7s step-end infinite',
                  '@keyframes nlq-blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
                }} />
              )}
              {sampleSize?.warning && (
                <Alert severity="info" icon={false} sx={{ py: 0.25, mt: 1, fontSize: '0.75rem', bgcolor: alpha(theme.palette.warning.main, 0.08), color: 'text.secondary' }}>
                  {sampleSize.warning}
                </Alert>
              )}
              {Array.isArray(charts) && charts.map((c, i) => (
                <ChartRenderer key={i} chart={c} />
              ))}
            </Box>
          )}
        </Box>

        {/* Follow-up chips */}
        {followUps.length > 0 && (
          <Box sx={{ px: 2.5, pb: 2, pt: 0.5 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem', display: 'block', mb: 0.75 }}>
              Ask a follow-up:
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.6}>
              {followUps.map(fu => (
                <Chip
                  key={fu}
                  label={fu}
                  size="small"
                  icon={<AutoAwesome sx={{ fontSize: '11px !important' }} />}
                  onClick={() => onFollowUp(fu)}
                  sx={{
                    cursor: 'pointer', fontWeight: 500, fontSize: '0.73rem', height: 24,
                    bgcolor: alpha(accent, 0.06), border: `1px solid ${alpha(accent, 0.18)}`,
                    color: 'text.primary',
                    '&:hover': { bgcolor: alpha(accent, 0.14), borderColor: alpha(accent, 0.38) },
                    transition: 'all 0.15s',
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Card>
    </Fade>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const Dashboard = () => {
  const theme    = useTheme();
  const { year } = useYear();
  const { user, token, isPaid, startCheckout } = useAuth();
  const inputRef = useRef(null);

  const [question,      setQuestion]      = useState('');
  const [answers,       setAnswers]       = useState(() => loadHistory());
  const [loading,       setLoading]       = useState(false);
  const [ollamaReady,   setOllamaReady]   = useState(null);
  const [placeholder,   setPlaceholder]   = useState(EXAMPLE_QUESTIONS[0]);
  const [authOpen,      setAuthOpen]      = useState(false);
  const [authTab,       setAuthTab]       = useState(0);
  const [paywall,       setPaywall]       = useState(null); // { tier, limit }

  const bottomRef = useRef(null);

  // Rotate placeholder
  useEffect(() => {
    let idx = 0;
    const t = setInterval(() => {
      idx = (idx + 1) % EXAMPLE_QUESTIONS.length;
      setPlaceholder(EXAMPLE_QUESTIONS[idx]);
    }, 4000);
    return () => clearInterval(t);
  }, []);

  // Check AI status
  useEffect(() => {
    nlqApi.status().then(r => setOllamaReady(r.configured)).catch(() => setOllamaReady(false));
  }, []);

  // ⌘K / Ctrl+K or '/' focuses the search bar from anywhere
  useEffect(() => {
    const handler = (e) => {
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) ||
          (e.key === '/' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA')) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Persist history on every change
  useEffect(() => { saveHistory(answers); }, [answers]);

  // Auto-scroll to bottom when answers update
  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [answers]);

  const handleAsk = useCallback(async (q = question) => {
    const trimmed = (typeof q === 'string' ? q : question).trim();
    if (!trimmed || loading) return;
    const id = Date.now();
    // Build conversation history from completed answers (last 6 exchanges = 12 messages)
    setAnswers(prev => {
      const history = prev
        .filter(a => !a.loading && !a.error && a.answer)
        .slice(-6)
        .flatMap(a => [
          { role: 'user',      content: a.question },
          { role: 'assistant', content: a.answer   },
        ]);
      handleAsk._history = history;
      return [...prev, { id, question: trimmed, answer: '', intent: null, charts: null, followUps: null, sampleSize: null, loading: true, error: null }];
    });
    setQuestion('');
    setLoading(true);
    try {
      const history = handleAsk._history || [];
      const res = await nlqApi.askStream(trimmed, year, history, {
        onToken: (text) => {
          setAnswers(prev => prev.map(a =>
            a.id === id ? { ...a, answer: (a.answer || '') + text } : a
          ));
        },
      });
      // `res` is the final `done` envelope
      setAnswers(prev => prev.map(a => a.id === id ? {
        ...a,
        answer: res.answer,
        intent: res.intent,
        charts: res.charts || null,
        followUps: res.follow_ups || null,
        sampleSize: res.sampleSize || null,
        loading: false,
      } : a));
    } catch (err) {
      if (err?.response?.status === 429 && err?.response?.data?.upgradeRequired) {
        const { tier, limit } = err.response.data;
        setAnswers(prev => prev.filter(a => a.id !== id));
        setPaywall({ tier, limit });
        setLoading(false);
        return;
      }
      const msg = err?.response?.data?.error || err?.message || 'Something went wrong. Please try again.';
      setAnswers(prev => prev.map(a => a.id === id ? { ...a, error: msg, loading: false } : a));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [question, loading, year]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAsk(); }
  };

  const handleFollowUp = useCallback((fu) => {
    setQuestion(fu);
    setTimeout(() => handleAsk(fu), 50);
  }, [handleAsk]);

  const handleClear = () => {
    setAnswers([]);
    localStorage.removeItem(HISTORY_KEY);
  };

  const accent     = theme.palette.primary.main;
  const isDark     = theme.palette.mode === 'dark';
  const hasAnswers = answers.length > 0;

  // ── Search bar (reused in both hero and pinned footer) ───────────────────
  const SearchBar = (
    <Box sx={{ width: '100%', maxWidth: 680 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center',
        border: `2px solid ${alpha(accent, loading ? 0.6 : 0.22)}`,
        borderRadius: 3,
        bgcolor: isDark ? alpha('#fff', 0.04) : 'background.paper',
        boxShadow: `0 2px 20px ${alpha(accent, 0.08)}`,
        transition: 'border-color 0.2s',
        '&:focus-within': { borderColor: alpha(accent, 0.65) },
      }}>
        <Search sx={{ ml: 2, color: 'text.disabled', fontSize: 22, flexShrink: 0 }} />
        <TextField
          inputRef={inputRef}
          fullWidth variant="standard"
          placeholder={placeholder}
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          multiline maxRows={3}
          InputProps={{ disableUnderline: true }}
          sx={{ mx: 1, '& textarea': { py: 1.5, fontSize: '1rem', lineHeight: 1.5, fontWeight: 500 } }}
        />
        <Tooltip title="⌘K" arrow>
          <span>
            <IconButton onClick={() => handleAsk()} disabled={!question.trim() || loading}
              sx={{
                mr: 0.5, width: 38, height: 38, borderRadius: 2,
                bgcolor: question.trim() ? accent : 'transparent',
                color: question.trim() ? 'white' : 'text.disabled',
                '&:hover': { bgcolor: question.trim() ? theme.palette.primary.dark : undefined },
                transition: 'all 0.2s',
              }}>
              {loading ? <CircularProgress size={18} color="inherit" /> : <Send sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {ollamaReady === false && (
        <Alert severity="warning" sx={{ mt: 1, py: 0.25, fontSize: '0.78rem', borderRadius: 2 }}>
          AI engine is warming up. Try again in a moment.
        </Alert>
      )}
    </Box>
  );

  // ── HERO (no answers yet) ────────────────────────────────────────────────
  if (!hasAnswers) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, pb: 6 }}>
        <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab={authTab} />
        <Fade in timeout={500}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
              <SportsBaseball sx={{ fontSize: 36, color: accent }} />
              <Typography variant="h3" fontWeight={900} sx={{
                fontSize: { xs: '2rem', sm: '2.75rem' },
                background: `linear-gradient(135deg, ${accent}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.5px',
              }}>
                The Cycle
              </Typography>
            </Box>
            <Typography variant="h6" color="text.secondary" fontWeight={400} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              Ask anything about baseball.
            </Typography>
          </Box>
        </Fade>

        {SearchBar}

        {paywall && (
          <PaywallBanner
            tier={paywall.tier}
            limit={paywall.limit}
            onSignUp={() => { setAuthTab(1); setAuthOpen(true); setPaywall(null); }}
            onUpgrade={async () => {
              if (!token) { setAuthTab(0); setAuthOpen(true); }
              else { try { await startCheckout(); } catch (_) {} }
              setPaywall(null);
            }}
            onDismiss={() => setPaywall(null)}
          />
        )}

        <Fade in timeout={600}>
          <Stack direction="row" flexWrap="wrap" gap={0.75} justifyContent="center" sx={{ mt: 3, maxWidth: 620 }}>
            {EXAMPLE_QUESTIONS.slice(0, 6).map(ex => (
              <Chip key={ex} label={ex} size="small"
                onClick={() => { setQuestion(ex); setTimeout(() => handleAsk(ex), 50); }}
                sx={{
                  cursor: 'pointer', fontWeight: 500, fontSize: '0.78rem',
                  bgcolor: alpha(accent, 0.06), border: `1px solid ${alpha(accent, 0.15)}`,
                  color: 'text.primary',
                  '&:hover': { bgcolor: alpha(accent, 0.14), borderColor: alpha(accent, 0.35) },
                  transition: 'all 0.15s',
                }}
              />
            ))}
          </Stack>
        </Fade>


      </Box>
    );
  }

  // ── CHAT MODE (has answers) ──────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' }, overflow: 'hidden' }}>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab={authTab} />

      {/* Paywall banner (chat mode) */}
      {paywall && (
        <Box sx={{ px: 2, pt: 2, flexShrink: 0 }}>
          <PaywallBanner
            tier={paywall.tier}
            limit={paywall.limit}
            onSignUp={() => { setAuthTab(1); setAuthOpen(true); setPaywall(null); }}
            onUpgrade={async () => {
              if (!token) { setAuthTab(0); setAuthOpen(true); }
              else { try { await startCheckout(); } catch (_) {} }
              setPaywall(null);
            }}
            onDismiss={() => setPaywall(null)}
          />
        </Box>
      )}

      {/* Scrollable message area */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 3, pb: 2 }}>
        <Box sx={{ maxWidth: 680, mx: 'auto' }}>
          {answers.map(a => (
            <AnswerBubble
              key={a.id}
              question={a.question}
              answer={a.answer}
              intent={a.intent}
              charts={a.charts}
              followUps={a.followUps}
              sampleSize={a.sampleSize}
              loading={a.loading}
              error={a.error}
              onFollowUp={handleFollowUp}
            />
          ))}
          {/* Anchor for auto-scroll */}
          <div ref={bottomRef} />
        </Box>
      </Box>

      {/* Pinned bottom input */}
      <Box sx={{
        flexShrink: 0,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
        bgcolor: isDark ? alpha('#121212', 0.95) : alpha('#fff', 0.97),
        backdropFilter: 'blur(12px)',
        px: 2, pt: 1.5, pb: { xs: 2.5, sm: 2 },
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
      }}>
        {SearchBar}
        <Box sx={{ width: '100%', maxWidth: 680, display: 'flex', justifyContent: 'flex-end', px: 0.5 }}>
          <Tooltip title="Clear chat" arrow>
            <IconButton size="small" onClick={handleClear} sx={{ color: 'text.disabled' }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
